<script setup lang="ts">
import { computed, ref } from 'vue';
import GenericModal from '@shared/ui/components/modals/GenericModal.vue';
import ButtonComponent from '@shared/ui/components/ButtonComponent.vue';
import { socialService } from '@features/social/api/social.service';
import { toast } from '@shared/utils/toast';

interface Props {
  modelValue: boolean;
  content: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  accept: [];
}>();

const show = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const formattedContent = computed(() => {
  if (!props.content) return '';
  return props.content;
});

const accepted = ref(false);
const isLoading = ref(false);

async function handleAccept() {
  if (!accepted.value) return;

  isLoading.value = true;
  try {
    await socialService.updateSocialUser({ termsAccepted: true });
    emit('accept');
    emit('update:modelValue', false);
  } catch (error) {
    console.error(error);
    toast.error(
      'Error al aceptar los términos. Por favor, inténtalo de nuevo.',
    );
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <GenericModal
    v-model="show"
    size="3xl"
    title="Términos y condiciones"
    title-class="text-2xl font-extrabold"
    icon="ph:shield-check"
    :is-icon="true"
    :hide-close-button="false"
    :close-to-click-outside="false"
    container-class="bg-cards-black"
    content-class="px-0 pb-0"
  >
    <div class="max-h-[55vh] overflow-y-auto pb-4">
      <div v-if="formattedContent">
        <div class="px-6 pb-4">
          <p class="mb-6 text-sm text-white/60">
            Por favor, lee y acepta nuestros términos y condiciones para seguir
            usando la plataforma.
          </p>
          <div v-safe-html="formattedContent" class="terms-ui"></div>
        </div>
      </div>
      <div v-else class="flex h-40 items-center justify-center">
        <IconifyIcon
          icon="eos-icons:loading"
          class="size-8 animate-spin text-primary"
        />
      </div>
    </div>

    <template #footer>
      <div class="border-t border-white/10 pt-4">
        <label
          class="flex cursor-pointer items-center gap-3 text-sm text-white/80"
        >
          <input
            v-model="accepted"
            type="checkbox"
            class="size-4 appearance-none rounded-full border border-primary bg-transparent checked:bg-primary"
          />
          He leído y acepto los
          <span class="font-bold text-white">Términos y condiciones</span>.
        </label>
        <div class="mt-4 flex justify-end">
          <ButtonComponent
            variant="primary"
            width="sm"
            :disabled="!accepted"
            :is-loading="isLoading"
            @click="handleAccept"
          >
            Aceptar y continuar
          </ButtonComponent>
        </div>
      </div>
    </template>
  </GenericModal>
</template>

<style scoped>
.terms-ui :deep(h2) {
  @apply mb-2 mt-6 text-lg font-semibold text-white;
}

.terms-ui :deep(p) {
  @apply mb-3 text-sm leading-relaxed text-white/80;
}

.terms-ui :deep(ul) {
  @apply mt-3 list-outside list-disc space-y-2 pl-6 marker:text-primary;
}

.terms-ui :deep(li) {
  @apply list-item text-sm text-white/80;
}

.terms-ui :deep(li p) {
  @apply inline;
}
</style>
